import { useFirebase } from 'containers/FirebaseContext';
import {
  collection,
  doc,
  getDocs,
  getDocsFromServer,
  query,
  setDoc,
  updateDoc,
  where,
  deleteDoc,
} from 'firebase/firestore';
import * as Sentry from '@sentry/react';
import { v4 as uuidv4 } from 'uuid';

export const useCollectionActivity = () => {
  const { firestore } = useFirebase();

  const createBlankDraftDoc = async (draftData) => {
    try {
      const assignmentId = draftData?.id;
      let docIdToUse = null;
  
      if (assignmentId) {
        const q = query(collection(firestore, import.meta.env.VITE_APP_STAGE?.trim() === 'prod' ? 'draft_activity_prod' : 'draft_activity'), where('id', '==', assignmentId));
        const querySnapshot = await getDocs(q);
  
        if (!querySnapshot.empty) {
          // A doc with matching field `id` exists, use its document ID
          docIdToUse = querySnapshot.docs[0].id;
        }
      }
  
      if (!docIdToUse) {
        // No existing doc found; generate a new one
        docIdToUse = uuidv4();
      }
      draftData.activity_type = 'assignment';
      draftData.questions = JSON.stringify(draftData.questions || []);
      const pageVal = draftData.page;
      if (pageVal != null && pageVal !== '') {
        draftData.page = Number(pageVal);
      }
      const docRef = doc(firestore, import.meta.env.VITE_APP_STAGE?.trim() === 'prod' ? 'draft_activity_prod' : 'draft_activity', docIdToUse);
      await setDoc(docRef, draftData, { merge: true });
  
      return docIdToUse;
    } catch (error) {
      console.error('Error in createBlankDraftDoc:', error);
      Sentry.captureException(`114 Error during Firebase create Draft blank ${error}.`);
      return null;
    }
  };

  const createOrUpdateDraftDoc = async (draftData) => {
    const { bookId, activity_type } = draftData;
    const pageRaw = draftData.page;
    const page = pageRaw != null && pageRaw !== '' ? Number(pageRaw) : pageRaw;
    try {
      // Validate required parameters
      if (!bookId || !page || !activity_type || bookId === 'undefined' || page === 'undefined' || activity_type === 'undefined') {
        console.warn('createOrUpdateDraftDoc: Missing or invalid required parameters', { bookId, page, activity_type });
        return null;
      }

      const q = query(
        collection(firestore, import.meta.env.VITE_APP_STAGE?.trim() === 'prod' ? 'draft_activity_prod' : 'draft_activity'),
        where('bookId', '==', bookId),
        where('page', '==', page),
        where('activity_type', '==', activity_type),
      );

      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const existingDoc = querySnapshot.docs[0];
        return {...existingDoc.data(), docId: existingDoc?.id};
      } else {
        const dataToCreate = { ...draftData, page };
        return await createBlankDraftDoc(dataToCreate);
      }
    } catch (error) {
      console.error('Error checking or creating/updating draft document:', error);
      Sentry.captureException(`115 Error during Firebase check/update Draft ${error}.`);
    }
  };

  const createDraftActivity = async (draftId, updatedData) => {
    if (!draftId) {
      console.error('Invalid draftId');
      return;
    }
    try {
      const questionsStr = typeof updatedData.questions === 'string' ? updatedData.questions : JSON.stringify(updatedData.questions || []);
      const pageVal = updatedData.page;
      const pageNorm = pageVal != null && pageVal !== '' ? Number(pageVal) : pageVal;
      const rawPayload = {
        displayName: updatedData.displayName != null ? String(updatedData.displayName) : '',
        questions: questionsStr,
        page: pageNorm,
        bookId: updatedData.bookId ?? '',
        chapter: updatedData.chapter ?? updatedData.bookId ?? '',
        typeId: updatedData.typeId ?? 'activity',
        colorCode: updatedData.colorCode ?? '',
        level: updatedData.level ?? '',
        category: updatedData.category ?? '',
        language_feedback: updatedData.language_feedback ?? '',
        is_exam: Boolean(updatedData.is_exam),
        is_unit_exam: Boolean(updatedData.is_unit_exam),
        unit: updatedData.unit ?? '',
        unit_exam: updatedData.unit_exam ?? '',
        ai_grading_config: updatedData.ai_grading_config && typeof updatedData.ai_grading_config === 'object' ? updatedData.ai_grading_config : {},
        rubrics_id: updatedData.rubrics_id ?? null,
        config_assignment: updatedData.config_assignment && typeof updatedData.config_assignment === 'object' ? updatedData.config_assignment : {},
        ...(updatedData.standards != null && Array.isArray(updatedData.standards) && updatedData.standards.length > 0 ? { standards: updatedData.standards } : {})
      };
      const mergePayload = Object.fromEntries(
        Object.entries(rawPayload).filter(([, v]) => v !== undefined)
      );
      const docRef = doc(
        firestore,
        import.meta.env.VITE_APP_STAGE?.trim() === 'prod' ? 'draft_activity_prod' : 'draft_activity',
        draftId,
      );
      await setDoc(docRef, mergePayload, { merge: true });
    } catch (error) {
      console.error('Error updating draft activity document:', error);
      Sentry.captureException(`114 Error during Firebase create Draft Activity ${error}.`);
    }
  };

  const fetchDraftActivities = async (bookId, pageId, type) => {
    try {
      // Validate required parameters - ensure they are valid and not undefined/null
      if (!bookId || !pageId || !type || bookId === 'undefined' || pageId === 'undefined' || type === 'undefined') {
        console.warn('fetchDraftActivities: Missing or invalid required parameters', { bookId, pageId, type });
        return [];
      }

      const pageNorm = pageId != null && pageId !== '' ? Number(pageId) : pageId;
      const collectionRef = collection(firestore, import.meta.env.VITE_APP_STAGE?.trim() === 'prod' ? 'draft_activity_prod' : 'draft_activity');
      const q = query(
        collectionRef,
        where('bookId', '==', bookId),
        where('page', '==', pageNorm),
        where('activity_type', '==', type),
      );
      const querySnapshot = await getDocsFromServer(q);

      const drafts = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const parsedQuestions = JSON.parse(data.questions || '[]');
        drafts.push({ docId: doc.id, ...data, questions: parsedQuestions, draft: true });
      });

      return drafts;
    } catch (error) {
      console.error('Error fetching draft activities:', error);
      Sentry.captureException(`115 Error during Firebase fetch Draft Activities ${error}.`);
      return [];
    }
  };

  const deleteDraftActivity = async (draftId) => {
    if (!draftId) {
      console.error('Invalid draftId for deletion');
      return;
    }
    try {
      const docRef = doc(firestore, import.meta.env.VITE_APP_STAGE?.trim() === 'prod' ? 'draft_activity_prod' : 'draft_activity', draftId);
      await deleteDoc(docRef);
    } catch (error) {
      console.error('Error deleting draft activity document:', error);
      Sentry.captureException(`116 Error during Firebase delete Draft Activity ${error}.`);
    }
  };

  return { createDraftActivity, fetchDraftActivities, createBlankDraftDoc, deleteDraftActivity, createOrUpdateDraftDoc };
};
